import "./Industry4-0.css";
import { useState } from "react";
import CardColorBlock from "../../../components/cards/CardColorBlock/CardColorBlock";
import HighlightText from "../../../components/texts/HighlightText/HighlightText";
import Title from "../../../components/common/Title/Title";
import SEO from "../../../components/common/SEO/SEO";
import Lightbox from "../../../components/articles/LightBox/LightBox";
import OptimizedImage from "../../../components/common/OptimizedImage/OptimizedImage";
import { Trans, useTranslation } from "react-i18next";

export default function Industry40() {
    const { t } = useTranslation(['industry4-0']);
    const [lightboxImage, setLightboxImage] = useState(null);

    const seo = t('seo', { returnObjects: true }) || {};
    const hero = t('hero', { returnObjects: true }) || {};
    const cards = t('cards', { returnObjects: true }) || {};
    const sections = t('sections', { returnObjects: true }) || {};
    const images = t('images', { returnObjects: true }) || {};

  return (
    <>
        <SEO
            title={seo.title}
            description={seo.description}
            url="/expertise/industrial4-0"
            image="/images/graphs/industry40.webp"
            jsonLd={[
              {
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": `${seo.title} - AIVHY`,
                "description": seo.description,
                "url": "/expertise/industrial4-0",
                "breadcrumb": {
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {
                      "@type": "ListItem",
                      "position": 1,
                      "name": "Home",
                      "item": "/"
                    },
                    {
                      "@type": "ListItem",
                      "position": 2,
                      "name": "Expertise",
                      "item": "/expertise"
                    },
                    {
                      "@type": "ListItem",
                      "position": 3,
                      "name": "Industry 4.0",
                      "item": "/expertise/industrial4-0"
                    }
                  ]
                }
              },
              {
                "@context": "https://schema.org",
                "@type": "Service",
                "name": "Industry 4.0 Implementation Services",
                "provider": {
                  "@type": "Organization",
                  "name": "AIVHY"
                },
                "description": "Industry 4.0 implementation and consultancy services for digital transformation and industrial automation",
                "serviceType": "Industry 4.0 Consulting",
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "Industry 4.0 Services",
                  "itemListElement": [
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Digital Transformation Consulting"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Industrial IoT Implementation"
                      }
                    },
                    {
                      "@type": "Offer",
                      "itemOffered": {
                        "@type": "Service",
                        "name": "Smart Factory Solutions"
                      }
                    }
                  ]
                }
              }
            ]}
        />
        <div className="main-container">
            <main className="head-content">
                <div className="column-content-container height-container">
                    <Title text={hero.title} size={["4rem", "3rem", "2rem"]} level="h1"/>
                    <HighlightText text={hero.tagline} size={["1.4rem", "1.2rem", "1rem"]} padding={["50px", "30px 24px", "20px"]} fontWeight="500" alignItems="flex-start"/>
                    <p className="paragraph-content">
                        <Trans
                            i18nKey="hero.body"
                            ns="industry4-0"
                            components={[
                                <a className="text-link" href="/solutions/implementation" />,
                                <a className="text-link" href="/solutions/consultancy" />,
                                <a className="text-link" href="/expertise/iiot" />,
                                <a className="text-link" href="/articles/guide-to-real-world-industry-4-0" />
                            ]}
                        />
                    </p>
                    <strong className="paragraph-content">{hero.cta}</strong>
                </div>
                <div className="cards-container">
                    <CardColorBlock image={"/images/icons/robot_arm_white.webp"} color={"var(--color-orange)"} title={cards?.industry?.title} text={cards?.industry?.text} />
                    <CardColorBlock iconClassName="fas fa-chart-line" iconStyle={{ fontSize: "70px", color: "white" }} color={"var(--color-blue)"} title={cards?.steps?.title} text={cards?.steps?.text} />
                    <CardColorBlock iconClassName="fas fa-euro-sign" iconStyle={{ fontSize: "70px", color: "white" }} color={"var(--color-blue)"} title={cards?.roi?.title} text={cards?.roi?.text} />
                    <CardColorBlock iconClassName="fas fa-layer-group" iconStyle={{ fontSize: "70px", color: "white" }} color={"var(--color-blue)"} title={cards?.holistic?.title} text={cards?.holistic?.text} />
                </div>
            </main>

            <section className="page-content-container">
                <div className="row-content-container">
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%"}}>
                        <Title text={sections?.whatIs?.title} size={["2rem", "1.8rem", "1.5rem"]} />
                        <p className="paragraph-content">
                            <Trans
                                i18nKey="sections.whatIs.body"
                                ns="industry4-0"
                                components={[<a className="text-link" href="/expertise/iiot" />]}
                            />
                        </p>
                        <p className="paragraph-content">
                            <Trans
                                i18nKey="sections.whatIs.more"
                                ns="industry4-0"
                                components={[<a className="text-link" href="/articles/real-world-industry-4-0" />]}
                            />
                        </p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%"}}>
                        <Title text={sections?.useCases?.title} size={["2rem", "1.8rem", "1.5rem"]}/>
                        <p className="paragraph-content">{sections?.useCases?.intro}</p>
                        <div className="page-highlight-container">
                            {Array.isArray(sections?.useCases?.bullets) && sections.useCases.bullets.map((b, idx) => (
                                <HighlightText key={idx} text={b} size={["1rem", "0.8rem"]}/>
                            ))}
                        </div>
                    </div>
                </div>
                <div style={{ display: "flex", width: "100%", justifyContent: "center" }}>
                    <img
                        className="industry4-process-image image-clickable"
                        src="/images/graphs/industry40.webp"
                        alt={'Industry 4.0 Logo'}
                        style={{ maxWidth: '100%', height: 'auto' }}
                        onClick={() => setLightboxImage({ src: "/images/graphs/industry40.webp", alt: "Industry 4.0 Logo" })}
                        role="button"
                        tabIndex={0}
                        aria-label="Open Industry 4.0 Logo"
                    />
                </div>
                <div className="row-content-container reverse-column" style={{ alignItems: "center" }}>
                    <div className="industry4-image-container">
                        <OptimizedImage
                            src="/images/industries/factory3.webp" 
                            className="factory3-mask" 
                            alt={images?.factoryAlt}
                        />
                    </div>
                    <div style={{ width: "100%"}}>
                        <Title text={sections?.kickstart?.title} size={["2rem", "1.8rem", "1.5rem"]}/>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <p className="paragraph-content">{sections?.kickstart?.p1}</p>
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.kickstart.p2"
                                    ns="industry4-0"
                                    components={[<a className="text-link" href="/solutions/kickstarter" />]}
                                />
                            </p>
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.kickstart.p3"
                                    ns="industry4-0"
                                    components={[<a className="text-link" href="/solutions/kickstarter" />]}
                                />
                            </p>
                        </div>
                    </div>
                </div>
                <div className="row-content-container" style={{ alignItems: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%"}}>
                        <Title text={sections?.approach?.title} size={["2rem", "1.8rem", "1.5rem"]} lineHeight="1"/>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: "10px" }}>
                            <p className="paragraph-content">{sections?.approach?.a1}</p>
                            <p className="paragraph-content">{sections?.approach?.a2}</p>
                            <p className="paragraph-content">
                                <Trans
                                    i18nKey="sections.approach.a3"
                                    ns="industry4-0"
                                    components={[<a className="text-link" href="/articles/guide-to-real-world-industry-4-0" />]}
                                />
                            </p>
                        </div>
                        <div style={{ marginLeft: "20px" }}>
                            {Array.isArray(sections?.approach?.bullets) && sections.approach.bullets.map((_, idx) => (
                                <HighlightText
                                    key={idx}
                                    text={
                                        <Trans
                                            i18nKey={`sections.approach.bullets.${idx}`}
                                            ns="industry4-0"
                                            components={[<strong />]}
                                        />
                                    }
                                    icon="fas fa-check"
                                    iconColor="var(--color-blue)"
                                    size={["1rem", "0.8rem"]}
                                />
                            ))}
                        </div>
                    </div>
                    <div style={{ width: "100%" }}>
                        <img
                            className="image-clickable"
                            src="/images/graphs/plan.webp"
                            alt={images?.planAlt || 'Aivhy Logo'}
                            style={{ maxWidth: '100%', height: 'auto' }}
                            onClick={() => setLightboxImage({ src: "/images/graphs/plan.webp", alt: "Plan Image" })}
                            role="button"
                            tabIndex={0}
                            aria-label="Open Plan Image"
                        />
                    </div>
                </div>
            </section>
        </div>
      <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
    </>
  );
}